import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Label } from '../../components/ui/label'
import { PasswordInput } from '../../components/ui/password-input'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { PasswordRequirements } from '../../components/ui/password-requirements'
import { isPasswordValid, PASSWORD_REQUIREMENTS_MESSAGE } from '../../lib/password-rules'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { processAuthLink, cleanupAuthLinkUrl, getRedirectUrlBasedOnRole } from '../../lib/auth-link-utils'
import { getUserProfile } from '../../lib/api/api' // For user profile
import { env } from '../../lib/env' // For environment variables

export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [needsMfa, setNeedsMfa] = useState(false)
  const [pendingPassword, setPendingPassword] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[ResetPasswordPage] useEffect - handleAuthFlow called')
      console.log('[ResetPasswordPage] Location details:', {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      })

      setIsLoading(true)

      try {
        // First check if we already have an active session
        console.log('[ResetPasswordPage] Checking for existing session first...')
        const {
          data: { session: existingSession },
        } = await supabase.auth.getSession()

        if (existingSession) {
          console.log('[ResetPasswordPage] Found existing active session')
          console.log('[ResetPasswordPage] Existing session details:', {
            userId: existingSession.user.id,
            email: existingSession.user.email,
            expiresAt: existingSession.expires_at,
          })
          setHasAccessToken(true)
          setIsLoading(false)
          return
        }

        console.log('[ResetPasswordPage] No existing session, processing auth link...')
        const result = await processAuthLink(supabase, location)

        console.log('[ResetPasswordPage] ProcessAuthLink result:', {
          success: result.success,
          hasSession: !!result.session,
          hasError: !!result.error,
          errorMessage: result.error?.message,
        })

        if (result.success && result.session) {
          console.log('[ResetPasswordPage] Successfully authenticated via processAuthLink')
          setHasAccessToken(true)
          cleanupAuthLinkUrl(window.location, true) // Preserve hash for routing
        } else {
          console.log('[ResetPasswordPage] ProcessAuthLink failed or returned no session')

          // Check if we have any auth parameters that should have worked
          const searchParams = new URLSearchParams(location.search)
          const hasCode = searchParams.has('code')
          const hasTokensInHash = location.hash.includes('access_token=')

          console.log('[ResetPasswordPage] Auth parameter check:', {
            hasCode,
            hasTokensInHash,
            searchParams: location.search,
            hash: location.hash,
          })

          if (hasCode || hasTokensInHash) {
            // We had auth parameters but they didn't work - this is an error
            console.error('[ResetPasswordPage] Auth parameters present but authentication failed')
            setHasAccessToken(false)

            const errorMessage =
              result.error?.message || 'Failed to process password reset link. The link may be expired or invalid.'

            toast({
              title: 'Authentication Failed',
              description: errorMessage,
              variant: 'destructive',
            })

            // Add delay before redirect to let user see the error
            setTimeout(() => {
              navigate('/auth/login', { replace: true })
            }, 3000)
          } else {
            // No auth parameters - user probably navigated directly to reset page
            console.log('[ResetPasswordPage] No auth parameters found - invalid access')
            setHasAccessToken(false)

            toast({
              title: 'Invalid Access',
              description: 'Please use the password reset link from your email to access this page.',
              variant: 'destructive',
            })

            navigate('/auth/forgot-password', { replace: true })
          }
        }
      } catch (error) {
        console.error('[ResetPasswordPage] Unexpected error in handleAuthFlow:', error)
        setHasAccessToken(false)

        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'

        toast({
          title: 'Error',
          description: `Failed to process password reset: ${errorMessage}`,
          variant: 'destructive',
        })

        navigate('/auth/login', { replace: true })
      } finally {
        setIsLoading(false)
      }
    }

    handleAuthFlow()
  }, [navigate, toast, location]) // Removed supabase from dependencies as it's stable

  const validatePassword = (password: string): string | null => {
    return isPasswordValid(password) ? null : PASSWORD_REQUIREMENTS_MESSAGE
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    // Validate password
    console.log('[ResetPasswordPage] handleSubmit called')

    console.log('[ResetPasswordPage] Validating password format')
    // Validate password
    const passwordError = validatePassword(password)
    if (passwordError) {
      console.log('[ResetPasswordPage] Password validation failed:', passwordError)
      setFormError(passwordError)
      return
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      console.log('[ResetPasswordPage] Passwords do not match.')
      setFormError('Passwords do not match')
      return
    }

    console.log('[ResetPasswordPage] Passwords match and validation passed. Setting isLoading to true.')
    setIsLoading(true)
    try {
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser')
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser:', error)
        // Check if the error is due to MFA requirement
        if (error.message.includes('AAL2 session is required')) {
          console.log('[ResetPasswordPage] MFA is required. Setting needsMfa to true.')
          setPendingPassword(password)
          setNeedsMfa(true)
          return
        }
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful.')
      // If we get here, password was updated successfully
      await handleSuccessfulPasswordReset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleSubmit:', errorMessage, error)
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessfulPasswordReset = async () => {
    console.log('[ResetPasswordPage] handleSuccessfulPasswordReset called')
    try {
      // 1. Check user profile BEFORE signing out (requires active session)
      console.log('[ResetPasswordPage] Fetching user profile before sign out.')
      const userProfile = await getUserProfile()
      console.log('[ResetPasswordPage] User profile fetched successfully')

      // 2. Determine if this is an entity user
      const isEntityUser = !!userProfile?.userRole && userProfile.userRole !== 'backoffice.admin'
      console.log('[ResetPasswordPage] Is entity user:', isEntityUser)

      // 3. Store the redirect URL before signout
      let redirectUrl = '/auth/login' // Default to admin login

      if (isEntityUser && env.VITE_USER_DASHBOARD_URL) {
        // Construct partner login URL
        redirectUrl = `${env.VITE_USER_DASHBOARD_URL}/auth/login`
        console.log('[ResetPasswordPage] Entity user detected, will redirect to partner login')
      } else {
        console.log('[ResetPasswordPage] Non-entity user or no partner URL, will redirect to admin login')
      }

      // 4. Sign out the user
      console.log('[ResetPasswordPage] Signing out user.')
      await supabase.auth.signOut()

      // 5. Show success message
      toast({
        title: 'Success',
        description: 'Your password has been reset successfully. Please log in with your new password.',
      })

      // 6. Perform the redirect
      if (isEntityUser && env.VITE_USER_DASHBOARD_URL) {
        // For external URLs, use window.location
        console.log('[ResetPasswordPage] Redirecting to partner login via window.location.href')
        window.location.href = redirectUrl
      } else {
        // For internal URLs, use the router's navigate
        console.log('[ResetPasswordPage] Redirecting to admin login via navigate')
        navigate(redirectUrl, { replace: true })
      }
    } catch (error) {
      // 7. Handle errors - fall back to admin login
      console.error('[ResetPasswordPage] Error during password reset redirect logic:', error)
      // Ensure user is signed out even on error
      try {
        await supabase.auth.signOut()
        console.log('[ResetPasswordPage] Signed out user after error in redirect logic.')
      } catch (signOutError) {
        console.error('[ResetPasswordPage] Error signing out user after error in redirect logic:', signOutError)
      }
      toast({
        title: 'Success',
        description: 'Your password has been reset successfully. Please log in with your new password.',
      })
      console.log('[ResetPasswordPage] Navigating to admin login as fallback after error.')
      navigate('/auth/login', { replace: true })
    }
  }

  const handleMfaComplete = async () => {
    console.log('[ResetPasswordPage] handleMfaComplete called')
    setIsLoading(true)
    try {
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser after MFA verification')
      // Try updating password again after MFA verification
      const { error } = await supabase.auth.updateUser({ password: pendingPassword })
      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser in handleMfaComplete:', error)
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful in handleMfaComplete.')
      // Reuse the updated handleSuccessfulPasswordReset logic
      await handleSuccessfulPasswordReset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleMfaComplete:', errorMessage, error)
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
      setNeedsMfa(false)
      setPendingPassword('')
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}
            />
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8" />
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md bg-black/60 p-10 border border-4 border-[#868686]/10 rounded-3xl">
                <h1 className="text-3xl font-bold mb-4 text-white">Set a New Password</h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  Choose a strong new password to keep your account secure.
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Reset Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-medium text-white">Set New Password</h1>
              </div>

              {isLoading && !hasAccessToken && !needsMfa ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="text-slate-400 font-light">Verifying your password reset link...</p>
                </div>
              ) : !hasAccessToken ? (
                <Alert variant="destructive">
                  <AlertDescription className="text-white">
                    Invalid or expired password reset link. Please request a new password reset.
                  </AlertDescription>
                </Alert>
              ) : needsMfa ? (
                <div className="space-y-4">
                  <Alert className="bg-slate-700 border-slate-600">
                    <AlertDescription className="text-white">
                      Two-factor authentication is required to complete your password reset.
                    </AlertDescription>
                  </Alert>
                  <TOTPVerify onComplete={handleMfaComplete} />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {formError && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertDescription className="text-white">{formError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password" className="text-sm font-light text-white">
                        New Password
                      </Label>
                      <PasswordInput
                        id="new-password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                      />
                      <PasswordRequirements password={password} className="text-slate-400" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password" className="text-sm font-light text-white">
                        Confirm Password
                      </Label>
                      <PasswordInput
                        id="confirm-password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20 pr-10"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
